import type { ExploreCourseData } from '@/app/types/interfaces';

export const COURSE_TABLE_COLUMNS = [
  { id: 'course', label: 'Course' },
  { id: 'credits', label: 'Credits' },
  { id: 'campus', label: 'Campus' },
  { id: 'open', label: 'Open' },
  { id: 'instructors', label: 'Instructors' },
  { id: 'professorRating', label: 'Professor rating' },
  { id: 'roomCapacity', label: 'Room seats' },
  { id: 'aiInsight', label: 'AI insights' },
  { id: 'difficulty', label: 'Difficulty' },
  { id: 'core', label: 'Core' },
  { id: 'department', label: 'Department' },
  { id: 'meetingTypes', label: 'Meeting types' },
  { id: 'schedulePatterns', label: 'Schedule patterns' },
  { id: 'wouldTakeAgain', label: 'Would take again' },
] as const;

export type CourseTableColumn = typeof COURSE_TABLE_COLUMNS[number]['id'];
export type CourseTableSort = { column: CourseTableColumn; direction: 'asc' | 'desc' };
export type CourseTableFilters = {
  creditsAtLeast: number;
  campus: string;
  openOnly: boolean;
  ratingAtLeast: number;
  roomSeatsAtLeast: number;
  insightsOnly: boolean;
};

export const DEFAULT_COURSE_TABLE_COLUMNS: CourseTableColumn[] = [
  'course', 'credits', 'campus', 'open', 'professorRating',
];

const columnIds = new Set<string>(COURSE_TABLE_COLUMNS.map(({ id }) => id));

export function parseCourseTableColumns(raw: unknown): CourseTableColumn[] {
  if (!Array.isArray(raw)) return [...DEFAULT_COURSE_TABLE_COLUMNS];
  const selected = raw.filter((value): value is CourseTableColumn => typeof value === 'string' && columnIds.has(value));
  const unique = [...new Set(selected)];
  return ['course', ...unique.filter((id) => id !== 'course')];
}

function numericCredit(course: ExploreCourseData): number | null {
  const value = Number.parseFloat(String(course.credits));
  return Number.isFinite(value) ? value : null;
}

function sortValue(course: ExploreCourseData, column: CourseTableColumn): string | number | null {
  switch (column) {
    case 'course': return course.fullTitle;
    case 'credits': return numericCredit(course);
    case 'campus': return (course.campuses ?? [course.campus]).join(', ');
    case 'open': return course.openSectionsCount;
    case 'instructors': return course.instructors?.join(', ') || null;
    case 'professorRating': return course.totalRatings ? course.averageRating : null;
    case 'roomCapacity': return course.classroomCapacity?.maxSeats ?? null;
    case 'aiInsight': return course.aiInsight?.label ?? null;
    case 'difficulty': return course.totalRatings ? course.averageDifficulty : null;
    case 'core': return course.coreRequirements.join(', ') || null;
    case 'department': return course.department || null;
    case 'meetingTypes': return course.meetingTypes?.join(', ') || null;
    case 'schedulePatterns': return course.schedulePatternCount ?? null;
    case 'wouldTakeAgain': return course.totalRatings ? course.averageWouldTakeAgain : null;
  }
}

export function filterAndSortCourseTable(courses: ExploreCourseData[], filters: CourseTableFilters, sort: CourseTableSort): ExploreCourseData[] {
  return courses.filter((course) => {
    const credits = numericCredit(course);
    return (!filters.creditsAtLeast || (credits !== null && credits >= filters.creditsAtLeast))
      && (!filters.campus || course.campuses?.includes(filters.campus) || course.campus === filters.campus)
      && (!filters.openOnly || course.openSectionsCount > 0)
      && (!filters.ratingAtLeast || (course.totalRatings > 0 && course.averageRating >= filters.ratingAtLeast))
      && (!filters.roomSeatsAtLeast || (course.classroomCapacity?.maxSeats ?? 0) >= filters.roomSeatsAtLeast)
      && (!filters.insightsOnly || Boolean(course.aiInsight));
  }).sort((a, b) => {
    const left = sortValue(a, sort.column);
    const right = sortValue(b, sort.column);
    // Missing ratings, room matches, and signals stay at the end in either direction.
    if (left === null && right !== null) return 1;
    if (right === null && left !== null) return -1;
    if (left === null && right === null) return a.baseId.localeCompare(b.baseId);
    const result = typeof left === 'number' && typeof right === 'number'
      ? left - right
      : String(left).localeCompare(String(right), undefined, { numeric: true });
    return (sort.direction === 'desc' ? -result : result) || a.baseId.localeCompare(b.baseId);
  });
}
